import React, { useState, useRef, useEffect } from 'react';
import { Link2, Copy, Check, X } from 'lucide-react';

interface MagicLinkModalProps {
  isOpen: boolean;
  magicLink: string | null;
  userName: string;
  onClose: () => void;
}

export function MagicLinkModal({ isOpen, magicLink, userName, onClose }: MagicLinkModalProps) {
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.select();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopy = async () => {
    if (!magicLink || !inputRef.current) return;

    inputRef.current.select();

    try {
      await navigator.clipboard.writeText(magicLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        alert('Kon niet kopiëren. Selecteer de link handmatig en kopieer met Cmd/Ctrl+C');
      }
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF6B35]/10 flex items-center justify-center flex-shrink-0">
                <Link2 className="w-5 h-5 text-[#FF6B35]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#231F20]">
                  Magic Link voor {userName}
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Deze link is 1 uur geldig
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Login Link
            </label>
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={magicLink || ''}
                readOnly
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={handleCopy}
                className={`px-4 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  copied
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-[#FF6B35] text-white hover:bg-[#E55A2B]'
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Gekopieerd
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Kopieer
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Let op:</strong> Deze link geeft directe toegang tot het account van {userName}.
              Deel deze link alleen via een veilig kanaal en verwijder de link na gebruik.
            </p>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Sluiten
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
